import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { emptyMilestone } from './constants';
import type { MilestoneItem } from './milestonesWidgetSettingsSchema';
import type { DemoDonationAmount } from './types';
import useDonationAmountData from './useDonationAmountData';
import type { Maybe } from '@/types';
function shouldUpdateTheMilestoneToPlay(initialMilestone: Maybe<Pick<MilestoneItem, 'id' | 'amountInCents'>>, nextMilestone: Pick<MilestoneItem, 'id' | 'amountInCents'>, milestoneToPlay: Maybe<Pick<MilestoneItem, 'id' | 'amountInCents'>>, anyDonationData: boolean, shouldCurrentPlay: boolean): boolean {
  // there is no milestone to play currently selected and the next milestone is different from the initial one
  const milestoneIsNullCase = milestoneToPlay === null && nextMilestone.id !== initialMilestone?.id;
  // the current milestone is simply the initial one and the next milestone is different
  const milestoneIsInitialAndNextIsDifferent = initialMilestone?.id === milestoneToPlay?.id && initialMilestone?.id !== nextMilestone.id;
  // the current milestone is ahead of the next milestone (happens upon updates)
  const milestoneIsAheadOfNext = !!milestoneToPlay && milestoneToPlay.amountInCents > nextMilestone.amountInCents;
  return (milestoneIsNullCase || milestoneIsInitialAndNextIsDifferent || milestoneIsAheadOfNext) &&
  // we need to have a valid next milestone
  !!nextMilestone.id &&
  // we need to have any donation data
  anyDonationData &&
  // we need to not be playing the current milestone
  !shouldCurrentPlay;
}
function shouldUpdateInitialIdFn({
  withDemoData,
  initialMilestone,
  nextMilestone,
  anyDonationData
}: {
  withDemoData: boolean | undefined;
  initialMilestone: MilestoneItem | null;
  nextMilestone: Pick<MilestoneItem, 'id'>;
  anyDonationData: boolean;
}) {
  return (
    // if we are not in demo mode we wait until the data is loaded (nextMilestone.id and anyDonationData) and then if the initial id is not set we set it to the next milestone id
    initialMilestone === null && (!withDemoData || !!nextMilestone.id && anyDonationData)
  );
}
function getNextMilestone(milestonesData: MilestoneItem[], totalAmountInCents: number, anyDonationData: boolean, withDemoData: boolean | undefined) {
  const fallbackMilestoneIndex = withDemoData ? 0 : milestonesData.length - 1;
  let nextMilestone: MilestoneItem | null = null;
  if (anyDonationData) {
    nextMilestone = milestonesData.find(milestone => milestone.amountInCents > totalAmountInCents) ?? null;
    nextMilestone ??= milestonesData[fallbackMilestoneIndex] ?? null;
  }
  return nextMilestone ?? emptyMilestone as MilestoneItem;
}
function useMilestones(fundraisingEventId, milestonesData, previewDonationData, withDemoData) {
  const $ = _c(13);
  const {
    data
  } = useDonationAmountData(fundraisingEventId, previewDonationData, withDemoData);
  const totalAmountInCents = data?.donated_amount_in_cents ?? -1;
  const anyDonationData = totalAmountInCents >= 0;
  const [milestoneId, setMilestoneId] = useState(null);
  const milestoneToPlay = milestoneId !== null ? milestonesData.find(milestone => milestone.id === milestoneId) ?? null : null;
  const [initialId, setInitialId] = useState(null);
  const initialMilestone = milestonesData.find(milestone_0 => milestone_0.id === initialId) ?? null;
  const thresholdReached = !!milestoneToPlay && totalAmountInCents >= milestoneToPlay.amountInCents;
  const nextMilestone = getNextMilestone(milestonesData, totalAmountInCents, anyDonationData, withDemoData);
  const currentMilestone = thresholdReached ? milestoneToPlay : nextMilestone;
  const first = !withDemoData && (initialId === null || initialId === milestoneId);
  const shouldPlay = !first && thresholdReached;
  const lastMilestone = milestonesData[milestonesData.length - 1];
  const lastMilestoneReached = !previewDonationData && lastMilestone && lastMilestone.amountInCents <= totalAmountInCents;
  if (shouldUpdateInitialIdFn({
    withDemoData,
    initialMilestone,
    nextMilestone,
    anyDonationData
  })) {
    let lastReachedMilestoneId = null;
    if (!withDemoData) {
      const lastReachedMilestone = milestonesData.findLast(milestone_1 => totalAmountInCents >= milestone_1.amountInCents);
      lastReachedMilestoneId = lastReachedMilestone?.id ?? null;
    }
    if (lastReachedMilestoneId !== initialId) {
      setInitialId(lastReachedMilestoneId);
    }
  }
  if (shouldUpdateTheMilestoneToPlay(initialMilestone, nextMilestone, milestoneToPlay, anyDonationData, shouldPlay)) {
    setMilestoneId(nextMilestone.id);
  }
  let t0;
  if ($[0] !== setInitialId || $[1] !== withDemoData) {
    t0 = function onPlaybackComplete(id) {
      setMilestoneId(null);
      if (withDemoData) {
        return;
      }
      if (id === null) {
        return;
      }
      setInitialId(id);
    };
    $[0] = setInitialId;
    $[1] = withDemoData;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const onPlaybackComplete = t0;
  let t1;
  if ($[3] !== initialId || $[4] !== setInitialId || $[5] !== withDemoData) {
    t1 = function onPlaybackStart(id_0) {
      setMilestoneId(id_0);
      if (withDemoData) {
        return;
      }
      if (id_0 !== initialId) {
        return;
      }
      setInitialId(null);
    };
    $[3] = initialId;
    $[4] = setInitialId;
    $[5] = withDemoData;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const onPlaybackStart = t1;
  let t2;
  if ($[7] !== currentMilestone || $[8] !== lastMilestoneReached || $[9] !== onPlaybackComplete || $[10] !== onPlaybackStart || $[11] !== shouldPlay) {
    t2 = {
      currentMilestone,
      shouldPlay,
      onPlaybackComplete,
      onPlaybackStart,
      lastMilestoneReached
    };
    $[7] = currentMilestone;
    $[8] = lastMilestoneReached;
    $[9] = onPlaybackComplete;
    $[10] = onPlaybackStart;
    $[11] = shouldPlay;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
}
export default useMilestones;